// v10/src/components/admin/aircraft/EquipmentTab.js
//
// R6 — Tab "Equipamiento" dentro de la tarjeta expandida de cada aeronave en
// AdminAircraft.js. Lista los items instalados (peso lb / brazo in / momento),
// permite editar/eliminar y abre el modal de importación desde Claude Vision.

import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileUp, Plus, Pencil, Trash2, Loader, AlertCircle, RefreshCw, X, Save,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import aircraftEquipmentService from '../../../services/aircraftEquipmentService';
import { useToast } from '../../../hooks/useToast';
import VisionImportModal from './VisionImportModal';

const CONFIDENCE_STYLES = {
  high:   { bg: 'rgba(34,197,94,0.12)',  color: '#16a34a', label: '🟢' },
  medium: { bg: 'rgba(245,158,11,0.12)', color: '#d97706', label: '🟡' },
  low:    { bg: 'rgba(249,115,22,0.16)', color: '#ea580c', label: '🟠' },
};

const fmtNumber = (n, digits = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(digits);
};

// ─── Edit Item Modal ─────────────────────────────────────────────
const EditItemModal = ({ item, aircraftId, onSaved, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [form, setForm] = useState({
    item_number: item.item_number || '',
    description: item.description || '',
    weight_lb: item.weight_lb ?? '',
    arm_in: item.arm_in ?? '',
    is_installed: item.is_installed !== false,
    is_required: !!item.is_required,
    notes: item.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.description.trim()) return;
    if (form.weight_lb === '' || form.arm_in === '') return;
    if (Number.isNaN(Number(form.weight_lb)) || Number.isNaN(Number(form.arm_in))) return;
    setSaving(true);
    try {
      const payload = {
        item_number: form.item_number.trim() || null,
        description: form.description.trim(),
        weight_lb: Number(form.weight_lb),
        arm_in: Number(form.arm_in),
        is_installed: form.is_installed,
        is_required: form.is_required,
        notes: form.notes.trim() || null,
      };
      await aircraftEquipmentService.update(aircraftId, item.id, payload);
      toast.success(t('equipment_saved'));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || t('equipment_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    width: '100%', padding: '8px 10px', borderRadius: 10, fontSize: 13,
    background: darkMode ? '#1f2937' : '#fff',
    color: darkMode ? '#fff' : '#0f172a',
    border: `1px solid ${darkMode ? '#374151' : '#e2e8f0'}`,
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480, borderRadius: 18, padding: 20,
          background: darkMode ? '#111827' : '#fff',
          color: darkMode ? '#fff' : '#0f172a',
          border: `1px solid ${darkMode ? '#1f2937' : '#e2e8f0'}`,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <h3 style={{ flex: 1, fontWeight: 700, fontSize: 16, margin: 0 }}>{t('equipment_edit_title')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: darkMode ? '#9ca3af' : '#64748b' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'grid', gap: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            {t('equipment_col_number')}
            <input value={form.item_number} onChange={e => setForm(f => ({ ...f, item_number: e.target.value }))} style={inp} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            {t('equipment_col_description')}*
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={inp} />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              {t('equipment_col_weight')}*
              <input type="number" step="0.01" value={form.weight_lb} onChange={e => setForm(f => ({ ...f, weight_lb: e.target.value }))} style={inp} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              {t('equipment_col_arm')}*
              <input type="number" step="0.01" value={form.arm_in} onChange={e => setForm(f => ({ ...f, arm_in: e.target.value }))} style={inp} />
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={form.is_installed} onChange={e => setForm(f => ({ ...f, is_installed: e.target.checked }))} />
            {t('equipment_field_installed')}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={form.is_required} onChange={e => setForm(f => ({ ...f, is_required: e.target.checked }))} />
            {t('equipment_field_required')}
          </label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            {t('equipment_field_notes')}
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} style={{ ...inp, resize: 'vertical' }} />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: darkMode ? '#374151' : '#f1f5f9', color: darkMode ? '#fff' : '#0f172a',
              fontWeight: 600, fontSize: 13 }}>
            {t('equipment_cancel')}
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1 }}>
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            {t('equipment_save')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Delete Confirm Modal ────────────────────────────────────────
const DeleteConfirmModal = ({ item, onConfirm, onCancel, darkMode, loading }) => {
  const { t } = useTranslation('admin');
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onCancel}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 380, borderRadius: 18, padding: 20,
          background: darkMode ? '#111827' : '#fff',
          color: darkMode ? '#fff' : '#0f172a',
        }}>
        <h3 style={{ fontWeight: 700, fontSize: 16, marginTop: 0 }}>{t('equipment_delete_title')}</h3>
        <p style={{ fontSize: 13, color: darkMode ? '#cbd5e1' : '#475569', margin: '8px 0 16px' }}>
          {t('equipment_delete_message', { description: item.description })}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={loading}
            style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: darkMode ? '#374151' : '#f1f5f9', color: darkMode ? '#fff' : '#0f172a',
              fontWeight: 600, fontSize: 13 }}>
            {t('equipment_cancel')}
          </button>
          <button onClick={onConfirm} disabled={loading}
            style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 13, opacity: loading ? 0.6 : 1 }}>
            {loading ? <Loader size={14} className="animate-spin" /> : t('equipment_delete_confirm')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Equipment Table ─────────────────────────────────────────────
const EquipmentTable = ({ items, onEdit, onDelete, darkMode }) => {
  const { t } = useTranslation('admin');

  const thStyle = {
    padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    color: darkMode ? '#9ca3af' : '#64748b',
    borderBottom: `1px solid ${darkMode ? '#1f2937' : '#e2e8f0'}`,
  };
  const tdStyle = {
    padding: '10px', fontSize: 13,
    borderBottom: `1px solid ${darkMode ? '#1f2937' : '#f1f5f9'}`,
    color: darkMode ? '#e5e7eb' : '#0f172a',
  };

  return (
    <div style={{ overflowX: 'auto', borderRadius: 12, border: `1px solid ${darkMode ? '#1f2937' : '#e2e8f0'}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
          <tr>
            <th style={thStyle}>{t('equipment_col_number')}</th>
            <th style={thStyle}>{t('equipment_col_description')}</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{t('equipment_col_weight')}</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{t('equipment_col_arm')}</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{t('equipment_col_moment')}</th>
            <th style={thStyle}>{t('equipment_col_confidence')}</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{t('equipment_col_actions')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => {
            const conf = it.source === 'vision' && it.confidence ? CONFIDENCE_STYLES[it.confidence] : null;
            return (
              <tr key={it.id}>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{it.item_number || '—'}</td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600 }}>{it.description}</div>
                  {it.is_required && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 6,
                      background: 'rgba(37,99,235,0.12)', color: '#2563eb', fontWeight: 700 }}>
                      {t('equipment_required_badge')}
                    </span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNumber(it.weight_lb)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNumber(it.arm_in)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: darkMode ? '#9ca3af' : '#64748b' }}>
                  {fmtNumber(it.moment_lb_in)}
                </td>
                <td style={tdStyle}>
                  {conf ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                      background: conf.bg, color: conf.color }}>
                      {conf.label} {t(`vision_import_legend_${it.confidence}`)}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, color: darkMode ? '#6b7280' : '#94a3b8' }}>
                      {it.source === 'manual' ? t('equipment_source_manual') : '—'}
                    </span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <button onClick={() => onEdit(it)} title={t('equipment_action_edit')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#2563eb' }}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => onDelete(it)} title={t('equipment_action_delete')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#ef4444', marginLeft: 4 }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Main Tab ────────────────────────────────────────────────────
const EquipmentTab = ({ aircraft, darkMode }) => {
  const { t } = useTranslation('admin');
  const toast = useToast();

  const [equipment, setEquipment] = useState([]);
  const [totals, setTotals] = useState({ items_count: 0, total_weight_lb: 0, total_moment_lb_in: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visionModalOpen, setVisionModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await aircraftEquipmentService.list(aircraft.id);
      const data = res?.data?.data;
      setEquipment(data?.equipment || []);
      setTotals(data?.totals || { items_count: 0, total_weight_lb: 0, total_moment_lb_in: 0 });
    } catch (err) {
      setError(err.response?.data?.message || t('equipment_load_error'));
    } finally {
      setLoading(false);
    }
  }, [aircraft.id, t]);

  useEffect(() => { load(); }, [load]);

  const handleVisionImportComplete = async (items, replaceExisting) => {
    try {
      const res = await aircraftEquipmentService.bulkCreate(aircraft.id, items, replaceExisting);
      const created = res?.data?.data?.created_count ?? items.length;
      toast.success(t('equipment_imported', { count: created }));
      setVisionModalOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || t('equipment_import_error'));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await aircraftEquipmentService.remove(aircraft.id, deleting.id);
      toast.success(t('equipment_deleted'));
      setDeleting(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || t('equipment_delete_error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const summaryText = t('equipment_summary', {
    count: totals.items_count,
    weight: Number(totals.total_weight_lb || 0).toFixed(1),
  });

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700, fontSize: 15, color: darkMode ? '#fff' : '#0f172a' }}>
            {t('equipment_title')}
          </h4>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
            {summaryText}
          </p>
        </div>
        <button onClick={() => setVisionModalOpen(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            borderRadius: 12, border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: '#fff',
            fontWeight: 700, fontSize: 13 }}>
          <FileUp size={14} /> {t('equipment_button_import')}
        </button>
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <Loader size={20} className="animate-spin" style={{ color: '#2563eb' }} />
        </div>
      )}

      {!loading && error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, borderRadius: 12,
          background: 'rgba(239,68,68,0.08)', color: '#b91c1c', fontSize: 13 }}>
          <AlertCircle size={16} />
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={load}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none',
              border: '1px solid rgba(185,28,28,0.3)', borderRadius: 8, padding: '4px 10px',
              color: '#b91c1c', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            <RefreshCw size={12} /> {t('equipment_retry')}
          </button>
        </div>
      )}

      {!loading && !error && equipment.length === 0 && (
        <div style={{ textAlign: 'center', padding: '28px 16px', borderRadius: 14,
          background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
          border: `1px dashed ${darkMode ? '#374151' : '#e2e8f0'}` }}>
          <Plus size={24} style={{ color: darkMode ? '#6b7280' : '#94a3b8', marginBottom: 6 }} />
          <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: darkMode ? '#e5e7eb' : '#0f172a' }}>
            {t('equipment_empty_title')}
          </p>
          <p style={{ margin: '4px 0 12px', fontSize: 12, color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
            {t('equipment_empty_description')}
          </p>
          <button onClick={() => setVisionModalOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              borderRadius: 12, border: 'none', cursor: 'pointer',
              background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 13 }}>
            <FileUp size={14} /> {t('equipment_button_import')}
          </button>
        </div>
      )}

      {!loading && !error && equipment.length > 0 && (
        <EquipmentTable items={equipment} onEdit={setEditing} onDelete={setDeleting} darkMode={darkMode} />
      )}

      <AnimatePresence>
        {visionModalOpen && (
          <VisionImportModal
            aircraftId={aircraft.id}
            aircraftLabel={`${aircraft.registration || ''} ${aircraft.manufacturer || ''} ${aircraft.model_name || ''}`.trim()}
            onComplete={handleVisionImportComplete}
            onClose={() => setVisionModalOpen(false)}
            darkMode={darkMode}
          />
        )}
        {editing && (
          <EditItemModal
            item={editing}
            aircraftId={aircraft.id}
            onSaved={async () => { setEditing(null); await load(); }}
            onClose={() => setEditing(null)}
            darkMode={darkMode}
          />
        )}
        {deleting && (
          <DeleteConfirmModal
            item={deleting}
            onConfirm={handleDelete}
            onCancel={() => setDeleting(null)}
            darkMode={darkMode}
            loading={deleteLoading}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default EquipmentTab;
